import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MailCheck, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDate, fmtDateTime, formatError } from "@/lib/api";
import { Field } from "../CompanyFormDialog";

function Card({ title, children, testId }) {
  return (
    <section className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid={testId}>
      <h3 className="text-lg font-semibold text-slate-800 mb-3">{title}</h3>
      {children}
    </section>
  );
}

function SafeModeCard() {
  const { user, appSettings, loadSettings } = useAuth();
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const on = appSettings?.email_safe_mode;
  const isOwner = user.email === appSettings?.safe_mode_owner;

  const change = async (enabled) => {
    try {
      await api.put("/admin/settings/safe-mode", { enabled, confirmation: phrase });
      toast.success(enabled ? "Email safe mode turned ON" : "Email safe mode turned OFF");
      setOpen(false); setPhrase("");
      loadSettings();
    } catch (e) { toast.error(formatError(e)); }
  };
  const testSend = async () => {
    try {
      const { data } = await api.post("/admin/test-email");
      data.status === "sent" ? toast.success(`Test email sent to ${data.actual_to.join(", ")}`) : toast.error(`Test email ${data.status}: ${data.error || ""}`);
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <Card title="Email safe mode" testId="safe-mode-card">
      <div className={`rounded-md border px-4 py-3 text-sm mb-4 ${on ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="safe-mode-status">
        <strong>{on ? "ON" : "OFF"}</strong> — {on ? `every email (To, CC and BCC) is redirected to ${appSettings?.safe_mode_recipient}, the subject starts with [TEST] and the original recipients are shown in a banner.` : "emails go to their real recipients. Payroll and blocked addresses are still never emailed."}
      </div>
      <div className="flex flex-wrap gap-2">
        {on && isOwner && <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => setOpen(true)} data-testid="safe-mode-turn-off-button"><ShieldAlert className="h-4 w-4 mr-1" /> Turn safe mode off…</Button>}
        {!on && <Button onClick={() => change(true)} data-testid="safe-mode-turn-on-button">Turn safe mode back on</Button>}
        <Button variant="outline" onClick={testSend} data-testid="send-test-email-button"><MailCheck className="h-4 w-4 mr-1" /> Send test email</Button>
      </div>
      {on && !isOwner && <p className="text-xs text-slate-500 mt-3" data-testid="safe-mode-owner-note">Only {appSettings?.safe_mode_owner} can turn safe mode off.</p>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="safe-mode-off-dialog">
          <DialogHeader>
            <DialogTitle>Turn email safe mode off?</DialogTitle>
            <DialogDescription>Emails will go to real contractors and recruiters. Only do this after the go-live checklist is complete. Type <strong>{appSettings?.safe_mode_off_phrase}</strong> to confirm.</DialogDescription>
          </DialogHeader>
          <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder={appSettings?.safe_mode_off_phrase} data-testid="safe-mode-confirm-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={phrase !== appSettings?.safe_mode_off_phrase} onClick={() => change(false)} data-testid="safe-mode-confirm-off-button">Turn off safe mode</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function MfaRequiredCard() {
  const { appSettings, loadSettings, refreshMe } = useAuth();
  const toggle = async (v) => {
    try { await api.put("/admin/settings/mfa-required", { enabled: v }); toast.success(v ? "MFA now required for all users" : "MFA now optional"); loadSettings(); refreshMe(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Card title="Two-step verification" testId="mfa-required-card">
      <label className="flex items-center gap-3 text-sm text-slate-700">
        <Switch checked={!!appSettings?.mfa_required} onCheckedChange={toggle} data-testid="mfa-required-switch" />
        Require MFA for all users (leave off until go-live)
      </label>
    </Card>
  );
}

function SuperGuaranteeCard({ settings, reload }) {
  const [form, setForm] = useState({ value: "", effective_from: "" });
  const rows = settings.filter((s) => s.key === "super_guarantee_percent");
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/settings/super-guarantee", form); toast.success("Super guarantee rate added"); setForm({ value: "", effective_from: "" }); reload(); } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <Card title="Super guarantee %" testId="sg-card">
      <table className="w-full text-sm mb-4">
        <thead><tr className="text-xs uppercase tracking-wider text-slate-400 text-left"><th className="py-2">Effective from</th><th>Rate</th><th>Added</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`sg-row-${r.id}`}><td className="py-2">{fmtDate(r.effective_from)}</td><td className="font-mono">{r.value}%</td><td className="text-slate-400">{fmtDateTime(r.created_at)}</td></tr>
          ))}
        </tbody>
      </table>
      <form onSubmit={add} className="flex flex-wrap items-end gap-3">
        <Field label="New rate %"><Input required value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="w-28" data-testid="sg-value-input" /></Field>
        <Field label="Effective from"><Input required type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} data-testid="sg-date-input" /></Field>
        <Button type="submit" variant="outline" data-testid="sg-add-button">Add rate</Button>
      </form>
    </Card>
  );
}

export function SettingsTab() {
  const [settings, setSettings] = useState([]);
  const load = useCallback(() => api.get("/admin/settings").then((r) => setSettings(r.data)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <SafeModeCard />
      <div className="space-y-6">
        <MfaRequiredCard />
        <SuperGuaranteeCard settings={settings} reload={load} />
      </div>
    </div>
  );
}
